import React, { useCallback, useEffect, useState } from 'react';
import { Box, Grid, Typography } from '@mui/material';
import MoneyIcon from '@mui/icons-material/AttachMoney';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { isLoadingSelector } from 'src/store/selectors/statusSelector';
import {
  selectTransactionBalance,
  selectTransactionOverdueAmount,
  selectTransactionsList,
  selectTransactionsListCount,
  selectTransactionsQueryOptions
} from 'src/store/selectors/transactionSelectors';
import {
  CLEAR_CONTRACT_TRANSACTIONS,
  downloadAccountStatmentResume,
  LOAD_CONTRACT_TRANSACTIONS,
  loadContractTransaction
} from 'src/store/actions/transactionActions';
import { useParams } from 'react-router-dom';
import { formatAmountWithDecimals } from 'src/utils/formatAmout';
import { InvoicePaymentStatusFilter } from 'src/utils/constants/transactions';
import useDebounce from 'src/hooks/useDebounce';
import useStyles from 'src/components/AccountStatement/styles';
import AccountStatementTable from 'src/components/AccountStatement/AccountStatementTable';
import TransactionsToolbar from 'src/components/AccountStatement/TransactionsToolbar';
import InvoiceDetailDrawer from 'src/components/AccountStatement/InvoiceDetailDrawer';

const AccountStatementDetail = () => {
  const { classes } = useStyles();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const transactionCount = useSelector(selectTransactionsListCount);
  const transactions = useSelector(selectTransactionsList);
  const balance = useSelector(selectTransactionBalance);
  const overdueAmount = useSelector(selectTransactionOverdueAmount);
  const queryOptions = useSelector(selectTransactionsQueryOptions);

  const { id } = useParams();
  const loading = useSelector(
    isLoadingSelector([LOAD_CONTRACT_TRANSACTIONS.action])
  );

  const [folio, setFolio] = useState('');
  const [paymentStatus, setPaymentStatus] = useState(
    InvoicePaymentStatusFilter.ALL
  );
  const [cutoffDate, setCutoffDate] = useState(null);
  const [viewingInvoiceId, setViewingInvoiceId] = useState(null);

  const debounceFilter = useDebounce(
    queryOpts =>
      dispatch(loadContractTransaction(id, { ...queryOpts, page: 0 })),
    400
  );

  const applyFilters = useCallback(
    overrides => {
      debounceFilter({
        ...queryOptions,
        page: 0,
        folio,
        paymentStatus:
          paymentStatus === InvoicePaymentStatusFilter.ALL
            ? undefined
            : paymentStatus,
        beforeDate: cutoffDate,
        ...overrides
      });
    },
    [debounceFilter, queryOptions, folio, paymentStatus, cutoffDate]
  );

  const handleFolioChange = useCallback(
    value => {
      setFolio(value);
      applyFilters({ folio: value });
    },
    [applyFilters]
  );

  const handlePaymentStatusChange = useCallback(
    value => {
      setPaymentStatus(value);
      applyFilters({
        paymentStatus:
          value === InvoicePaymentStatusFilter.ALL ? undefined : value
      });
    },
    [applyFilters]
  );

  const handleApplyCutoffDate = useCallback(
    value => {
      setCutoffDate(value);
      applyFilters({ beforeDate: value });
    },
    [applyFilters]
  );

  useEffect(() => {
    dispatch(loadContractTransaction(id, { page: 0 }));
    return () => {
      dispatch({ type: CLEAR_CONTRACT_TRANSACTIONS });
    };
  }, [dispatch, id]);

  const handleMovementsChangePage = useCallback(
    (_, value) => {
      dispatch(loadContractTransaction(id, { page: value }));
    },
    [dispatch, id]
  );

  const handleViewInvoice = useCallback(invoiceId => {
    setViewingInvoiceId(invoiceId);
  }, []);

  const handleCloseDrawer = useCallback(() => {
    setViewingInvoiceId(null);
  }, []);

  const handleDrawerMutated = useCallback(() => {
    dispatch(loadContractTransaction(id, queryOptions));
  }, [dispatch, id, queryOptions]);

  const handleExcelExport = useCallback(() => {
    dispatch(downloadAccountStatmentResume(id, queryOptions));
  }, [dispatch, id, queryOptions]);

  return (
    <Box mt={3}>
      <Grid container spacing={4}>
        <Grid item xs={12}>
          <Box className={classes.headerRow}>
            <Box>
              <Typography
                style={{ fontWeight: '400', textTransform: 'capitalize' }}
                color="primary"
                variant="h1"
              >
                {t('NAVIGATION.ACCOUNTING_STATE')}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t('COMMON.AMOUNTS_WITH_IVA')}
              </Typography>
            </Box>
          </Box>
        </Grid>
        <Grid item xs={12}>
          <Box className={classes.cardsRow}>
            <Box className={classes.transaction}>
              <Box className={classes.transactionIcon}>
                <MoneyIcon fill="#28A745" />
              </Box>
              <Grid item>
                <Typography
                  style={{ marginBottom: 8 }}
                  color="textPrimary"
                  component="p"
                  variant="body2"
                >
                  {t('COMMON.BALANCE')}
                </Typography>
                <Typography color="primary" component="h2" variant="h2">
                  {formatAmountWithDecimals(balance)}
                </Typography>
              </Grid>
            </Box>
            <Box className={classes.transaction}>
              <Box className={classes.overdueIcon}>!</Box>
              <Grid item>
                <Typography
                  style={{ marginBottom: 8 }}
                  color="textPrimary"
                  component="p"
                  variant="body2"
                >
                  {t('TRANSACTION.OVERDUE_AMOUNT')}
                </Typography>
                <Typography color="primary" component="h2" variant="h2">
                  {formatAmountWithDecimals(overdueAmount)}
                </Typography>
              </Grid>
            </Box>
          </Box>
        </Grid>
        <Grid item lg={12}>
          <TransactionsToolbar
            folio={folio}
            onFolioChange={handleFolioChange}
            showContractFilter={false}
            paymentStatus={paymentStatus}
            onPaymentStatusChange={handlePaymentStatusChange}
            cutoffDate={cutoffDate}
            onApplyCutoffDate={handleApplyCutoffDate}
            onDownload={handleExcelExport}
          />
          <AccountStatementTable
            className={classes.table}
            count={transactionCount}
            page={queryOptions.page}
            loading={loading}
            handleChangePage={handleMovementsChangePage}
            transactions={transactions}
            onView={handleViewInvoice}
            showContractColumn={false}
            showRentNumberColumn={false}
          />
        </Grid>
      </Grid>
      <InvoiceDetailDrawer
        open={Boolean(viewingInvoiceId)}
        invoiceId={viewingInvoiceId}
        cutoffDate={cutoffDate}
        onClose={handleCloseDrawer}
        onMutated={handleDrawerMutated}
      />
    </Box>
  );
};

export default AccountStatementDetail;
